import {
  Directive,
  ElementRef,
  AfterViewInit,
  OnDestroy,
  inject,
  output,
  input,
} from '@angular/core';
import { fromEvent, Subject } from 'rxjs';
import { auditTime, takeUntil } from 'rxjs/operators';

/** Share of the scroll height from an edge that counts as near it. */
const DEFAULT_EDGE_THRESHOLD = 0.2;
const SCROLL_AUDIT_MS = 80;
/** A wheel delta below this is a nudge, not a request for the next page. */
const WHEEL_PAGE_DELTA = 50;
const BOTTOM_TOLERANCE_PX = 5;

@Directive({
  selector: '[fr8botChatScroll]',
  standalone: true,
})
export class ChatScrollDirective implements AfterViewInit, OnDestroy {
  private elRef = inject<ElementRef<HTMLElement>>(ElementRef);

  readonly threshold = input(DEFAULT_EDGE_THRESHOLD);

  readonly scrolledUp = output<void>();
  readonly scrolled = output<void>();
  readonly bottomStateChange = output<boolean>();

  private destroy$ = new Subject<void>();
  private last = 0;
  private init = false;

  private topLock = false;
  private bottomLock = false;

  private isAtBottom = true;

  ngAfterViewInit(): void {
    const el = this.elRef.nativeElement;

    fromEvent(el, 'scroll')
      .pipe(auditTime(SCROLL_AUDIT_MS), takeUntil(this.destroy$))
      .subscribe(() => this.onScroll());

    fromEvent<WheelEvent>(el, 'wheel')
      .pipe(takeUntil(this.destroy$))
      .subscribe((event) => this.onWheel(event));

    queueMicrotask(() => {
      this.last = el.scrollTop;
      this.init = true;
    });
  }

  private onWheel(event: WheelEvent): void {
    if (!this.init) return;

    const el = this.elRef.nativeElement;

    if (event.deltaY > WHEEL_PAGE_DELTA && el.scrollTop >= -1) {
      this.scrolled.emit();
    }

    if (
      event.deltaY < -WHEEL_PAGE_DELTA &&
      el.scrollTop <= -(el.scrollHeight - el.clientHeight) + 1
    ) {
      this.scrolledUp.emit();
    }
  }

  private onScroll(): void {
    if (!this.init) return;

    const el = this.elRef.nativeElement;

    const scrollTop = el.scrollTop;
    const max = el.scrollHeight - el.clientHeight;
    const min = -max;
    const offset = max * this.threshold();

    const up = scrollTop < this.last;
    const down = scrollTop > this.last;

    this.last = scrollTop;

    const nearTop = scrollTop <= min + offset;
    const nearBottom = scrollTop >= -offset;

    if (!nearTop) this.topLock = false;
    if (!nearBottom) this.bottomLock = false;

    if (nearTop && up && !this.topLock) {
      this.topLock = true;
      this.scrolledUp.emit();
    }

    if (nearBottom && down && !this.bottomLock) {
      this.bottomLock = true;
      this.scrolled.emit();
    }

    const isBottomExact = Math.abs(scrollTop) <= BOTTOM_TOLERANCE_PX;

    if (this.isAtBottom !== isBottomExact) {
      this.isAtBottom = isBottomExact;
      this.bottomStateChange.emit(this.isAtBottom);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
